import { FormControl, InputLabel, ListSubheader, MenuItem, SxProps, Theme } from "@mui/material";
import PSelect from "./PSelect";
import Flex from "./Flex";
import { Checkmark } from "./Icons";
import theme from "../theme";
import { useCallback, useContext } from "react";
import { useAppSelector } from "../redux/hooks";
import { selectFormFields, selectFormPagesVisible, selectFormSections } from "../redux/slices/form.slice";
import { CaseFormContext } from "../contexts/CaseFormContext";
import type { FormFieldValuesUI } from "Backend";
import type { FormField, FormPage, FormSection } from "generated/graphql";
import { UseCaseForm } from "helpers/hooks/useCaseForm";
import DescriptionTooltip from "components/FormField/DescriptionTooltip";

const fieldItem = (body: { formValues: FormFieldValuesUI[]; field: FormField; prefix?: number }) => {
  const { formValues, field, prefix } = body;
  const fValue = formValues.find((v) => v.fieldId === field.id);
  const answered = fValue?.yesNoValue !== undefined;

  return (
    <MenuItem key={field.id} value={field.id}>
      <Flex flexDirection="row" alignItems="center">
        {answered ? (
          <Checkmark htmlColor={theme.palette.grey["400"]} size={16} style={{ marginRight: "8px" }} />
        ) : (
          <Flex width={16} height={16} marginRight="8px" />
        )}
        {prefix ?? field.outlinePrefix}. {field.name}
      </Flex>
    </MenuItem>
  );
};

const sectionBasedFieldMenuItems = (body: {
  caseForm: UseCaseForm;
  fields: FormField[];
  sections: FormSection[];
  formPages: FormPage[];
  filterOption?: (field: FormField) => boolean;
}) => {
  const { caseForm, fields, sections, formPages, filterOption } = body;
  const formValues = caseForm ? caseForm.serverFieldValues : [];

  const menuGroups = (sectionId: number) => {
    return fields
      .filter((f) => f.sectionId === sectionId)
      .filter(filterOption ? filterOption : (f) => f)
      .slice(0)
      .sort((a, b) => a.rank - b.rank)
      .map((f) => fieldItem({ formValues, field: f }));
  };

  return formPages.length < 2
    ? sections.length < 2
      ? fields.map((f, index) => fieldItem({ formValues, field: f, prefix: index + 1 }))
      : sections.map((section) => {
          const menuGroup = [<ListSubheader key={section.id}>{section.name}</ListSubheader>];
          return menuGroup.concat(...menuGroups(section.id));
        })
    : formPages.map((fp) => {
        return sections
          .filter((s) => s.formPageId === fp.id)
          .slice(0)
          .map((s) => {
            const sectionMenuGroup = [
              <ListSubheader key={s.id}>
                {fp.name} - {s.name}
              </ListSubheader>,
            ];
            return sectionMenuGroup.concat(...menuGroups(s.id));
          });
      });
};

interface FormFieldSelectProps {
  fieldId: number | undefined;
  changeFieldId: (fieldId: number | undefined) => void;
  allowNone?: boolean;
  sx?: SxProps<Theme>;
  descriptionTooltipSx?: SxProps<Theme>;
  label?: string;
  noneLabel?: string;
  filterOption?: (field: FormField) => boolean;
  dataPendo?: string;
  className?: string;
}
export const FormFieldSelect = (props: FormFieldSelectProps) => {
  const { fieldId, changeFieldId, allowNone, sx, label, noneLabel, dataPendo, descriptionTooltipSx, className } = props;
  const fields = useAppSelector(selectFormFields);
  const caseForm = useContext(CaseFormContext);
  const sections = useAppSelector(selectFormSections);
  const formPages = useAppSelector(selectFormPagesVisible);

  const noneItem = allowNone ? (
    <MenuItem key="none" value={-1}>
      {noneLabel}
    </MenuItem>
  ) : null;
  const renderValue = useCallback(
    (fieldId: unknown) => {
      const field = fields.find((f) => f.id === fieldId);
      return field ? `${field.outlinePrefix}. ${field.name}` : noneLabel;
    },
    [fields]
  );

  const description = fields.find((f) => f.id === fieldId)?.description;

  return (
    <>
      <FormControl
        sx={{
          ...sx,
          minWidth: "200px",
        }}
        className={className}
        data-pendo={`${dataPendo}-field-select`}>
        {label && <InputLabel id="form-field-select">{label}</InputLabel>}
        <PSelect
          labelId="form-field-select"
          label={label || ""}
          value={fieldId || -1}
          renderValue={renderValue}
          onChange={(evt) => {
            const v = evt.target.value as number | undefined;
            if (v === -1) {
              changeFieldId(undefined);
            } else {
              changeFieldId(v);
            }
          }}>
          {noneItem}
          {sectionBasedFieldMenuItems({
            filterOption: props.filterOption,
            caseForm,
            fields,
            sections,
            formPages,
          })}
        </PSelect>
      </FormControl>
      <DescriptionTooltip description={description} sx={{ mx: 1, ...(descriptionTooltipSx || {}) }} />
    </>
  );
};

interface MultipleFormFieldSelectProps {
  fieldIds: number[];
  changeFieldIds: (fieldIds: number[]) => void;
  sx?: SxProps<Theme>;
  label?: string;
  filterOption?: (field: FormField) => boolean;
}

export const MultipleFormFieldSelect = (props: MultipleFormFieldSelectProps) => {
  const { fieldIds, changeFieldIds, sx, label } = props;
  const caseForm = useContext(CaseFormContext);
  const fields = useAppSelector(selectFormFields);
  const sections = useAppSelector(selectFormSections);
  const formPages = useAppSelector(selectFormPagesVisible);

  const renderValue = useCallback(
    (fieldIds) => {
      return fieldIds.map((fieldId) => {
        const field = fields.find((f) => f.id === fieldId);
        return field ? `${field.outlinePrefix}. ${field.name}` : fieldId;
      });
    },
    [fields]
  );
  return (
    <FormControl size="small" sx={sx}>
      {label && <InputLabel id="multiple-form-field-select">{label}</InputLabel>}
      <PSelect
        multiple
        labelId="form-field-select"
        label={label || ""}
        sx={{ minWidth: "200px" }}
        value={fieldIds}
        renderValue={renderValue}
        onChange={(evt) => {
          const v = evt.target.value as number[];
          changeFieldIds(v);
        }}>
        {sectionBasedFieldMenuItems({
          filterOption: props.filterOption,
          caseForm,
          sections,
          formPages,
          fields,
        })}
      </PSelect>
    </FormControl>
  );
};
